// Map drawing for the minimap and the map screen (decision 0048: "a map screen that fills in as places are found";
// the HUD's minimap slot). Both draw the same three layers on a 2D canvas: the game's picture of the world (areas and
// lines in its palette), painted once into an offscreen canvas; the fog over cells not yet revealed, repainted only
// when the discovery's version moves; and the markers (waypoints, points of interest, the objective, the player) on
// top, upright. Map-up is north (−z on the ground) unless the minimap rotates with the camera. The view transform is
// pure (testable in Node); the canvas work is the browser's. The game converts its world into a `MapPicture`, so this
// package knows nothing of layouts.
import { h, type VNode } from "preact";
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type El = VNode<any>;
import { useEffect, useRef } from "preact/hooks";

/** A shape on the ground plane, metres (the same shapes as a world layout's). */
export type MapShape =
  | { kind: "circle"; x: number; z: number; r: number }
  | { kind: "rect"; x0: number; z0: number; x1: number; z1: number }
  | { kind: "polygon"; points: readonly (readonly [number, number])[] };

/** What the map draws of the world, in the game's colours. */
export interface MapPicture {
  bounds: { x0: number; z0: number; x1: number; z1: number };
  /** The ground's colour (outside every area). */
  ground: string;
  /** Filled areas, in order (later on top). */
  areas: { shape: MapShape; fill: string }[];
  /** Polylines: paths, streams. */
  lines: { points: readonly (readonly [number, number])[]; width: number; colour: string }[];
  /** Small squares: buildings and props (centre, half-size m, yaw). */
  blocks?: { x: number; z: number; half: number; yaw: number; colour: string }[];
}

/** The fog's source: a grid of revealed cells over the bounds (a discovery). */
export interface MapFog {
  readonly cols: number;
  readonly rows: number;
  readonly cell: number;
  readonly bounds: { x0: number; z0: number };
  readonly version: number;
  revealed(col: number, row: number): boolean;
}

/** A marker drawn over the map. */
export interface MapMarker {
  id: string;
  x: number;
  z: number;
  icon: "player" | "waypoint" | "poi" | "objective" | "enemy";
  colour?: string;
  /** Drawn faint (seen but not reached). */
  dim?: boolean;
  /** The player's heading (radians clockwise from map-up), for its arrow. */
  heading?: number;
}

/** How a view looks at the map: its centre (m), scale (px per m), the heading drawn up (radians clockwise from north,
 *  0 for north-up) and its size (px). */
export interface MapView {
  cx: number;
  cz: number;
  scale: number;
  heading: number;
  width: number;
  height: number;
}

/** A ground point to view px. */
export function worldToView(v: MapView, x: number, z: number): [number, number] {
  const dx = (x - v.cx) * v.scale;
  const dz = (z - v.cz) * v.scale;
  const c = Math.cos(-v.heading);
  const s = Math.sin(-v.heading);
  return [v.width / 2 + dx * c - dz * s, v.height / 2 + dx * s + dz * c];
}

/** View px to a ground point. */
export function viewToWorld(v: MapView, px: number, py: number): [number, number] {
  const dx = px - v.width / 2;
  const dy = py - v.height / 2;
  const c = Math.cos(v.heading);
  const s = Math.sin(v.heading);
  return [v.cx + (dx * c - dy * s) / v.scale, v.cz + (dx * s + dy * c) / v.scale];
}

/** Zoom by `factor` about view point (px, py), keeping the ground under it still; the scale held in [min, max]. */
export function zoomAt(v: MapView, factor: number, px: number, py: number, min: number, max: number): MapView {
  const scale = Math.min(max, Math.max(min, v.scale * factor));
  const [wx, wz] = viewToWorld(v, px, py);
  const n = { ...v, scale };
  const [nx, nz] = viewToWorld(n, px, py);
  return { ...n, cx: v.cx + (wx - nx), cz: v.cz + (wz - nz) };
}

/** Keep a view's centre inside the bounds. */
export function clampView(v: MapView, b: MapPicture["bounds"]): MapView {
  return { ...v, cx: Math.min(b.x1, Math.max(b.x0, v.cx)), cz: Math.min(b.z1, Math.max(b.z0, v.cz)) };
}

function shapePath(ctx: CanvasRenderingContext2D, s: MapShape, px: (x: number, z: number) => [number, number], k: number): void {
  ctx.beginPath();
  if (s.kind === "circle") {
    const [x, y] = px(s.x, s.z);
    ctx.arc(x, y, s.r * k, 0, Math.PI * 2);
  } else if (s.kind === "rect") {
    const [x0, y0] = px(s.x0, s.z0);
    const [x1, y1] = px(s.x1, s.z1);
    ctx.rect(Math.min(x0, x1), Math.min(y0, y1), Math.abs(x1 - x0), Math.abs(y1 - y0));
  } else {
    s.points.forEach((p, i) => {
      const [x, y] = px(p[0], p[1]);
      if (i) ctx.lineTo(x, y);
      else ctx.moveTo(x, y);
    });
    ctx.closePath();
  }
}

/** Paint the picture once, north-up, at `pxPerM`: the static layer every view draws from. */
export function paintPicture(doc: Document, pic: MapPicture, pxPerM: number): HTMLCanvasElement {
  const b = pic.bounds;
  const c = doc.createElement("canvas");
  c.width = Math.ceil((b.x1 - b.x0) * pxPerM);
  c.height = Math.ceil((b.z1 - b.z0) * pxPerM);
  const ctx = c.getContext("2d");
  if (!ctx) return c;
  const px = (x: number, z: number): [number, number] => [(x - b.x0) * pxPerM, (z - b.z0) * pxPerM];
  ctx.fillStyle = pic.ground;
  ctx.fillRect(0, 0, c.width, c.height);
  for (const a of pic.areas) {
    shapePath(ctx, a.shape, px, pxPerM);
    ctx.fillStyle = a.fill;
    ctx.fill();
  }
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  for (const l of pic.lines) {
    ctx.beginPath();
    l.points.forEach((p, i) => {
      const [x, y] = px(p[0], p[1]);
      if (i) ctx.lineTo(x, y);
      else ctx.moveTo(x, y);
    });
    ctx.strokeStyle = l.colour;
    ctx.lineWidth = Math.max(1, l.width * pxPerM);
    ctx.stroke();
  }
  for (const k of pic.blocks ?? []) {
    ctx.save();
    const [x, y] = px(k.x, k.z);
    ctx.translate(x, y);
    ctx.rotate(-k.yaw);
    ctx.fillStyle = k.colour;
    const s = Math.max(2, k.half * pxPerM);
    ctx.fillRect(-s, -s, s * 2, s * 2);
    ctx.restore();
  }
  return c;
}

/** The fog layer: one px per cell, opaque where unrevealed. Repaint when `fog.version` moves. */
export function paintFog(doc: Document, fog: MapFog, colour: [number, number, number, number], into?: HTMLCanvasElement): HTMLCanvasElement {
  const c = into ?? doc.createElement("canvas");
  c.width = fog.cols;
  c.height = fog.rows;
  const ctx = c.getContext("2d");
  if (!ctx) return c;
  const img = ctx.createImageData(fog.cols, fog.rows);
  for (let r = 0; r < fog.rows; r++)
    for (let q = 0; q < fog.cols; q++) {
      const i = (r * fog.cols + q) * 4;
      const open = fog.revealed(q, r);
      img.data[i] = colour[0];
      img.data[i + 1] = colour[1];
      img.data[i + 2] = colour[2];
      img.data[i + 3] = open ? 0 : colour[3];
    }
  ctx.putImageData(img, 0, 0);
  return c;
}

/** The layers a view draws: the painted picture and the fog canvas, with the scale the picture was painted at. */
export interface MapLayers {
  picture: MapPicture;
  base: HTMLCanvasElement;
  pxPerM: number;
  fog: HTMLCanvasElement | null;
  fogCell: number;
}

/** Draw a view of the map: the picture and fog (turned with the heading), then the markers, upright. */
export function drawMapView(ctx: CanvasRenderingContext2D, layers: MapLayers, v: MapView, markers: readonly MapMarker[], o: { circle?: boolean; background?: string } = {}): void {
  const b = layers.picture.bounds;
  ctx.save();
  ctx.clearRect(0, 0, v.width, v.height);
  if (o.circle) {
    ctx.beginPath();
    ctx.arc(v.width / 2, v.height / 2, Math.min(v.width, v.height) / 2, 0, Math.PI * 2);
    ctx.clip();
  }
  ctx.fillStyle = o.background ?? "#1d2a1f";
  ctx.fillRect(0, 0, v.width, v.height);
  ctx.save();
  ctx.translate(v.width / 2, v.height / 2);
  ctx.rotate(-v.heading);
  ctx.scale(v.scale, v.scale);
  ctx.translate(-v.cx, -v.cz);
  ctx.imageSmoothingEnabled = true;
  ctx.drawImage(layers.base, b.x0, b.z0, layers.base.width / layers.pxPerM, layers.base.height / layers.pxPerM);
  if (layers.fog) ctx.drawImage(layers.fog, b.x0, b.z0, layers.fog.width * layers.fogCell, layers.fog.height * layers.fogCell);
  ctx.restore();
  for (const m of markers) drawMarker(ctx, v, m);
  ctx.restore();
}

function drawMarker(ctx: CanvasRenderingContext2D, v: MapView, m: MapMarker): void {
  const [x, y] = worldToView(v, m.x, m.z);
  ctx.save();
  ctx.translate(x, y);
  ctx.globalAlpha = m.dim ? 0.55 : 1;
  ctx.lineWidth = 2;
  ctx.strokeStyle = "rgba(20,24,18,.9)";
  if (m.icon === "player") {
    ctx.rotate((m.heading ?? 0) - v.heading);
    ctx.beginPath();
    ctx.moveTo(0, -8);
    ctx.lineTo(6, 6);
    ctx.lineTo(0, 3);
    ctx.lineTo(-6, 6);
    ctx.closePath();
    ctx.fillStyle = m.colour ?? "#fbf6e8";
    ctx.fill();
    ctx.stroke();
  } else if (m.icon === "waypoint") {
    ctx.beginPath();
    ctx.moveTo(0, -7);
    ctx.lineTo(7, 0);
    ctx.lineTo(0, 7);
    ctx.lineTo(-7, 0);
    ctx.closePath();
    ctx.fillStyle = m.colour ?? "#9fd8ff";
    ctx.fill();
    ctx.stroke();
  } else if (m.icon === "objective") {
    ctx.beginPath();
    for (let i = 0; i < 10; i++) {
      const a = (i / 10) * Math.PI * 2 - Math.PI / 2;
      const r = i % 2 ? 3.5 : 8;
      if (i) ctx.lineTo(Math.cos(a) * r, Math.sin(a) * r);
      else ctx.moveTo(Math.cos(a) * r, Math.sin(a) * r);
    }
    ctx.closePath();
    ctx.fillStyle = m.colour ?? "#e9c46a";
    ctx.fill();
    ctx.stroke();
  } else {
    ctx.beginPath();
    ctx.arc(0, 0, m.icon === "enemy" ? 3.5 : 5, 0, Math.PI * 2);
    ctx.fillStyle = m.colour ?? (m.icon === "enemy" ? "#ff5b3a" : "#fbf6e8");
    ctx.fill();
    ctx.stroke();
  }
  ctx.restore();
}

/** The minimap: a round canvas `size` css px across showing `range` metres round the player, north-up or turning with
 *  the camera. It redraws only when the player moves or turns, the fog changes or the markers do. */
export interface Minimap {
  readonly el: HTMLCanvasElement;
  /** Draw this frame (skipped when nothing moved). `heading`: the camera's, radians clockwise from north. */
  frame(player: { x: number; z: number; heading: number }, markers: readonly MapMarker[]): void;
  setRotate(on: boolean): void;
  /** Redraws so far (canvas draws, never a Preact render). */
  draws(): number;
  state(): { rotate: boolean; view: MapView | null; draws: number; fogVersion: number };
}

/** Build a minimap over the game's map layers. */
export function createMinimap(o: { layers: MapLayers; fog?: MapFog | null; fogColour?: [number, number, number, number]; size?: number; range?: number; rotate?: boolean; doc?: Document }): Minimap {
  const doc = o.doc ?? document;
  const size = o.size ?? 148;
  const dpr = Math.min(2, doc.defaultView?.devicePixelRatio ?? 1);
  const el = doc.createElement("canvas");
  el.width = Math.round(size * dpr);
  el.height = Math.round(size * dpr);
  el.style.width = `${size}px`;
  el.style.height = `${size}px`;
  el.className = "rpg-minimap";
  el.dataset.minimap = "";
  el.style.borderRadius = "50%";
  el.style.boxShadow = "0 0 0 3px rgba(29,42,31,.85), 0 2px 10px rgba(0,0,0,.35)";
  const ctx = el.getContext("2d");
  let rotate = o.rotate ?? true;
  let draws = 0;
  let fogVersion = -1;
  let last = "";
  let view: MapView | null = null;
  const range = o.range ?? 60;
  const fogColour = o.fogColour ?? [29, 42, 31, 235];
  return {
    el,
    frame(p, markers) {
      if (o.fog && o.fog.version !== fogVersion) {
        o.layers.fog = paintFog(doc, o.fog, fogColour, o.layers.fog ?? undefined);
        o.layers.fogCell = o.fog.cell;
        fogVersion = o.fog.version;
        last = "";
      }
      const heading = rotate ? p.heading : 0;
      const key = `${p.x.toFixed(1)},${p.z.toFixed(1)},${heading.toFixed(3)},${p.heading.toFixed(2)},${markers.map((m) => `${m.id}:${m.x.toFixed(1)},${m.z.toFixed(1)},${m.dim ? 1 : 0}`).join(";")}`;
      if (key === last || !ctx) return;
      last = key;
      view = { cx: p.x, cz: p.z, scale: el.width / 2 / range, heading, width: el.width, height: el.height };
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      drawMapView(ctx, o.layers, view, [...markers, { id: "player", x: p.x, z: p.z, icon: "player", heading: p.heading }], { circle: true });
      draws++;
    },
    setRotate(on) {
      if (on === rotate) return;
      rotate = on;
      last = "";
    },
    draws: () => draws,
    state: () => ({ rotate, view: view ? { ...view } : null, draws, fogVersion }),
  };
}

/** What a map panel exposes once mounted (pan, zoom, its view and redraw count). */
export interface MapPanelHandle {
  view(): MapView;
  pan(dxPx: number, dyPx: number): void;
  zoom(factor: number): void;
  centre(x: number, z: number): void;
  draws(): number;
}

/** Props of {@link MapPanel}. */
export interface MapPanelProps {
  layers: MapLayers;
  fog?: MapFog | null;
  fogColour?: [number, number, number, number];
  markers: () => readonly MapMarker[];
  /** Where the view starts. */
  start: { x: number; z: number; scale: number };
  /** The scale's range (px per m). */
  minScale: number;
  maxScale: number;
  /** The canvas's accessible name (a translated string). */
  label: string;
  /** Read the pads (the right stick pans, the shoulders zoom); default `navigator.getGamepads()`. */
  pads?: () => readonly ({ axes: readonly number[]; buttons: readonly { pressed: boolean; value: number }[] } | null)[];
  /** Called once mounted with the panel's handle (the game's handle and its specs). */
  bind?: (h: MapPanelHandle | null) => void;
}

/** The map screen's map: a focusable canvas the player pans and zooms with the keyboard (arrows, + and −, when it has
 *  focus), the pad (right stick, shoulders), the mouse (drag, wheel) and touch (drag, pinch). Drawn imperatively on
 *  each change: panning never re-renders the screen. */
export function MapPanel(p: MapPanelProps): El {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const props = useRef(p);
  props.current = p;
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const doc = el.ownerDocument;
    const win = doc.defaultView!;
    const dpr = Math.min(2, win.devicePixelRatio || 1);
    const ctx = el.getContext("2d");
    let draws = 0;
    let fogVersion = -1;
    const b = p.layers.picture.bounds;
    let v: MapView = { cx: p.start.x, cz: p.start.z, scale: p.start.scale * dpr, heading: 0, width: 1, height: 1 };
    const size = (): void => {
      const r = el.getBoundingClientRect();
      el.width = Math.max(1, Math.round(r.width * dpr));
      el.height = Math.max(1, Math.round(r.height * dpr));
      v = { ...v, width: el.width, height: el.height };
    };
    const draw = (): void => {
      if (!ctx) return;
      const fog = props.current.fog;
      if (fog && fog.version !== fogVersion) {
        p.layers.fog = paintFog(doc, fog, p.fogColour ?? [29, 42, 31, 240], p.layers.fog ?? undefined);
        p.layers.fogCell = fog.cell;
        fogVersion = fog.version;
      }
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      drawMapView(ctx, p.layers, v, props.current.markers());
      draws++;
    };
    const minS = p.minScale * dpr;
    const maxS = p.maxScale * dpr;
    const handle: MapPanelHandle = {
      view: () => ({ ...v, scale: v.scale / dpr }),
      pan(dx, dy) {
        const [x, z] = viewToWorld(v, v.width / 2 - dx * dpr, v.height / 2 - dy * dpr);
        v = clampView({ ...v, cx: x, cz: z }, b);
        draw();
      },
      zoom(f) {
        v = clampView(zoomAt(v, f, v.width / 2, v.height / 2, minS, maxS), b);
        draw();
      },
      centre(x, z) {
        v = clampView({ ...v, cx: x, cz: z }, b);
        draw();
      },
      draws: () => draws,
    };
    size();
    draw();
    const onKey = (e: KeyboardEvent): void => {
      const step = 40;
      const k = e.key;
      if (k === "+" || k === "=") handle.zoom(1.25);
      else if (k === "-" || k === "_") handle.zoom(0.8);
      else if (doc.activeElement === el && k === "ArrowLeft") handle.pan(step, 0);
      else if (doc.activeElement === el && k === "ArrowRight") handle.pan(-step, 0);
      else if (doc.activeElement === el && k === "ArrowUp") handle.pan(0, step);
      else if (doc.activeElement === el && k === "ArrowDown") handle.pan(0, -step);
      else return;
      e.preventDefault();
    };
    doc.addEventListener("keydown", onKey);
    // drag to pan, pinch and wheel to zoom
    const pointers = new Map<number, { x: number; y: number }>();
    let pinch = 0;
    const onDown = (e: PointerEvent): void => {
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      el.setPointerCapture?.(e.pointerId);
      if (pointers.size === 2) {
        const [a, c] = [...pointers.values()];
        pinch = Math.hypot(a!.x - c!.x, a!.y - c!.y);
      }
    };
    const onMove = (e: PointerEvent): void => {
      const was = pointers.get(e.pointerId);
      if (!was) return;
      const now = { x: e.clientX, y: e.clientY };
      pointers.set(e.pointerId, now);
      if (pointers.size === 1) handle.pan(now.x - was.x, now.y - was.y);
      else if (pointers.size === 2) {
        const [a, c] = [...pointers.values()];
        const d = Math.hypot(a!.x - c!.x, a!.y - c!.y);
        if (pinch > 0 && d > 0) handle.zoom(d / pinch);
        pinch = d;
      }
    };
    const onUp = (e: PointerEvent): void => {
      pointers.delete(e.pointerId);
      pinch = 0;
    };
    const onWheel = (e: WheelEvent): void => {
      e.preventDefault();
      handle.zoom(e.deltaY < 0 ? 1.15 : 1 / 1.15);
    };
    el.addEventListener("pointerdown", onDown);
    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerup", onUp);
    el.addEventListener("pointercancel", onUp);
    el.addEventListener("wheel", onWheel, { passive: false });
    // the pad: the right stick pans, the shoulders zoom (the d-pad and left stick still move focus between items)
    const readPads = p.pads ?? (() => (typeof navigator !== "undefined" && navigator.getGamepads ? navigator.getGamepads() : []));
    let raf = 0;
    let lastT = win.performance.now();
    const tick = (): void => {
      const t = win.performance.now();
      const dt = Math.min(0.1, (t - lastT) / 1000);
      lastT = t;
      for (const pad of readPads()) {
        if (!pad) continue;
        const ax = pad.axes[2] ?? 0;
        const ay = pad.axes[3] ?? 0;
        if (Math.hypot(ax, ay) > 0.2) handle.pan(-ax * 420 * dt, -ay * 420 * dt);
        if (pad.buttons[5]?.pressed) handle.zoom(1 + 1.5 * dt);
        if (pad.buttons[4]?.pressed) handle.zoom(1 / (1 + 1.5 * dt));
      }
      // the fog may have moved under an open map (it doesn't in play: the sim is paused under screens)
      if (props.current.fog && props.current.fog.version !== fogVersion) draw();
      raf = win.requestAnimationFrame(tick);
    };
    raf = win.requestAnimationFrame(tick);
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => (size(), draw())) : null;
    ro?.observe(el);
    p.bind?.(handle);
    return () => {
      doc.removeEventListener("keydown", onKey);
      el.removeEventListener("pointerdown", onDown);
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerup", onUp);
      el.removeEventListener("pointercancel", onUp);
      el.removeEventListener("wheel", onWheel);
      win.cancelAnimationFrame(raf);
      ro?.disconnect();
      props.current.bind?.(null);
    };
  }, []);
  return h("canvas", { ref, class: "rpg-map", role: "img", "aria-label": p.label, tabIndex: 0, "data-nav-keys": "", "data-map": "" });
}
